import { useCallback, useEffect, useState } from 'react'
import { Coins, MapPin, Phone, ShoppingBag, UserRound, UsersRound } from 'lucide-react'
import { Papel, formatarReais } from '@du/shared'
import {
  Badge, Button, EmptyState, Icone, Input, Modal, SectionTitle, Select, Skeleton, StatTile, useAviso,
} from '@du/ui'
import { api, ErroApi } from '../lib/api.js'
import { useSessao } from '../lib/sessao.js'

type Resumo = {
  total: number
  novos30: number
  recorrentes: number
  taxaRecorrencia: number
  ticketMedioCents: number
}

type Cliente = {
  id: string
  nome: string
  telefone: string
  pedidos: number
  totalGastoCents: number
  ticketMedioCents: number
  ultimoPedidoEm: string | null
  desdeEm: string
  optOutZap: boolean
}

type Detalhe = Cliente & {
  telefoneBruto: string
  /** O detalhe traz criadoEm; a listagem traz desdeEm. */
  criadoEm: string
  aceiteLgpdEm: string | null
  enderecos: {
    id: string; rua: string; numero: string; complemento: string | null
    referencia: string | null; bairroTexto: string; cep: string | null
    padrao: boolean; bairro: { nome: string } | null
  }[]
  pedidos_: never
  resumo: { pedidos: number; totalGastoCents: number; ticketMedioCents: number }
  pontos: {
    ativa: boolean
    ativaDesde: string | null
    saldo: number
    vencendo: { pontos: number; em: string } | null
    extrato: {
      id: string
      tipo: 'GANHO' | 'RESGATE' | 'EXPIRACAO' | 'AJUSTE'
      pontos: number
      criadoEm: string
      numeroDia: number | null
      motivo: string | null
      porQuem: string | null
    }[]
  }
}

type PedidoDoCliente = {
  id: string; numeroDia: number; criadoEm: string; status: string
  tipo: string; totalCents: number; canal: string
  itens: { nomeSnapshot: string; quantidade: number }[]
}

const dia = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—'

export function Clientes() {
  const { usuario } = useSessao()
  const { avisar } = useAviso()
  const [resumo, setResumo] = useState<Resumo | null>(null)
  const [lista, setLista] = useState<Cliente[]>([])
  const [busca, setBusca] = useState('')
  const [ordem, setOrdem] = useState('recentes')
  const [carregando, setCarregando] = useState(true)
  const [aberto, setAberto] = useState<(Detalhe & { pedidos: PedidoDoCliente[] }) | null>(null)

  const [ajustando, setAjustando] = useState<{ pontos: string; motivo: string } | null>(null)
  const [salvandoAjuste, setSalvandoAjuste] = useState(false)

  const gestor = usuario?.papel === Papel.ADMIN || usuario?.papel === Papel.GERENTE

  const pontosAjuste = Number(ajustando?.pontos ?? '')
  const ajusteValido =
    Number.isInteger(pontosAjuste) &&
    pontosAjuste !== 0 &&
    (ajustando?.motivo.trim().length ?? 0) >= 3

  const lancarAjuste = async () => {
    if (!aberto || !ajustando || !ajusteValido) return
    setSalvandoAjuste(true)
    try {
      await api(`/v1/clientes/${aberto.id}/pontos`, {
        method: 'POST',
        corpo: { pontos: pontosAjuste, motivo: ajustando.motivo.trim() },
      })
      avisar({ tom: 'sucesso', texto: 'Ajuste lançado no extrato do cliente.' })
      setAjustando(null)
      // Recarrega a ficha para o saldo e o extrato já mostrarem a linha nova.
      const atualizado = await api<Detalhe & { pedidos: PedidoDoCliente[] }>(
        `/v1/clientes/${aberto.id}`,
      )
      setAberto(atualizado)
    } catch (e) {
      avisar({
        tom: 'erro',
        texto: 'Não consegui lançar',
        detalhe: e instanceof ErroApi ? e.message : undefined,
      })
    } finally {
      setSalvandoAjuste(false)
    }
  }

  const carregar = useCallback(async () => {
    try {
      const q = new URLSearchParams({ ordem })
      if (busca.trim()) q.set('busca', busca.trim())
      const [r, c] = await Promise.all([
        api<Resumo>('/v1/clientes/resumo'),
        api<Cliente[]>(`/v1/clientes?${q}`),
      ])
      setResumo(r)
      setLista(c)
    } catch (e) {
      avisar({ tom: 'erro', texto: 'Não consegui carregar', detalhe: e instanceof ErroApi ? e.message : undefined })
    } finally {
      setCarregando(false)
    }
  }, [avisar, busca, ordem])

  // Busca com espera: digitar "Maria" dispararia cinco consultas sem isto.
  useEffect(() => {
    const t = setTimeout(() => void carregar(), busca ? 350 : 0)
    return () => clearTimeout(t)
  }, [carregar, busca])

  if (carregando) return <div className="pagina-gestao"><Skeleton altura={280} /></div>

  return (
    <div className="pagina-gestao">
      <section className="bloco-gestao">
        <SectionTitle variante="laranja" tamanho="sm">Clientes</SectionTitle>

        <div className="grade-stats">
          <StatTile rotulo="Cadastrados" valor={String(resumo?.total ?? 0)} tom="marca" />
          <StatTile rotulo="Novos" valor={String(resumo?.novos30 ?? 0)} apoio="últimos 30 dias" />
          <StatTile
            rotulo="Voltaram"
            valor={`${resumo?.taxaRecorrencia ?? 0}%`}
            apoio={`${resumo?.recorrentes ?? 0} com 2+ pedidos`}
            tom={(resumo?.taxaRecorrencia ?? 0) >= 30 ? 'marca' : 'neutro'}
          />
          <StatTile rotulo="Ticket médio" valor={formatarReais(resumo?.ticketMedioCents ?? 0)} />
        </div>

        <div className="form-linha">
          <Input
            rotulo="Buscar"
            placeholder="Nome ou telefone"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <Select
            rotulo="Ordenar por"
            value={ordem}
            onChange={(e) => setOrdem(e.target.value)}
            opcoes={[
              { valor: 'recentes', rotulo: 'Pediram por último' },
              { valor: 'maisPedidos', rotulo: 'Mais pedidos' },
              { valor: 'maiorValor', rotulo: 'Gastaram mais' },
            ]}
          />
        </div>

        {lista.length === 0 ? (
          <EmptyState
            icone={UsersRound}
            titulo={busca ? 'Ninguém com esse nome ou número' : 'Nenhum cliente ainda'}
            texto={busca ? undefined : 'O cadastro acontece sozinho no primeiro pedido.'}
          />
        ) : (
          <ul className="lista-simples">
            {lista.map((c) => (
              <li key={c.id}>
                <span>
                  <Icone de={UserRound} tamanho={16} /> {c.nome}
                  {c.optOutZap ? <Badge tom="alerta">sem WhatsApp</Badge> : null}
                </span>
                <span className="lista-simples__fraco">
                  {c.telefone} · {c.pedidos} pedido{c.pedidos === 1 ? '' : 's'} ·{' '}
                  {formatarReais(c.totalGastoCents)} · último {dia(c.ultimoPedidoEm)}
                </span>
                <span className="lista-simples__acoes">
                  <Button
                    variante="ghost"
                    tamanho="sm"
                    onClick={async () => {
                      try {
                        setAberto(await api(`/v1/clientes/${c.id}`))
                      } catch (e) {
                        avisar({ tom: 'erro', texto: 'Não consegui abrir', detalhe: e instanceof ErroApi ? e.message : undefined })
                      }
                    }}
                  >
                    Ver
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Modal aberto={aberto !== null} aoFechar={() => setAberto(null)} titulo={aberto?.nome ?? ''}>
        {aberto ? (
          <div className="form-modal">
            <div className="grade-stats">
              <StatTile rotulo="Pedidos" valor={String(aberto.resumo.pedidos)} tom="marca" />
              <StatTile rotulo="Total gasto" valor={formatarReais(aberto.resumo.totalGastoCents)} />
              <StatTile rotulo="Ticket médio" valor={formatarReais(aberto.resumo.ticketMedioCents)} />
            </div>

            <p className="lista-simples__fraco">
              {/*
                `desdeEm` existe só no payload da LISTAGEM; o detalhe traz
                `criadoEm`. A ficha mostrava "cliente desde —" para todo mundo.
              */}
              <Icone de={Phone} tamanho={14} /> {aberto.telefone} · cliente desde{' '}
              {dia(aberto.criadoEm ?? aberto.desdeEm)}
              {aberto.aceiteLgpdEm ? ` · aceitou a política em ${dia(aberto.aceiteLgpdEm)}` : ''}
            </p>

            {gestor ? (
              <div className="linha-acoes">
                <Button
                  variante="ghost"
                  tamanho="sm"
                  onClick={async () => {
                    try {
                      await api(`/v1/clientes/${aberto.id}`, {
                        method: 'PATCH',
                        corpo: { optOutZap: !aberto.optOutZap },
                      })
                      setAberto({ ...aberto, optOutZap: !aberto.optOutZap })
                      await carregar()
                    } catch (e) {
                      avisar({ tom: 'erro', texto: 'Não consegui salvar', detalhe: e instanceof ErroApi ? e.message : undefined })
                    }
                  }}
                >
                  {aberto.optOutZap ? 'Voltar a enviar WhatsApp' : 'Parar de enviar WhatsApp'}
                </Button>
              </div>
            ) : null}

            <SectionTitle variante="vazado" tamanho="sm">Endereços</SectionTitle>
            {aberto.enderecos.length === 0 ? (
              <p className="nota-alerta">Só pediu para retirar no balcão.</p>
            ) : (
              <ul className="lista-simples">
                {aberto.enderecos.map((e) => (
                  <li key={e.id}>
                    <span>
                      <Icone de={MapPin} tamanho={14} /> {e.rua}, {e.numero}
                      {e.complemento ? ` — ${e.complemento}` : ''}
                      {e.padrao ? <Badge tom="sucesso">padrão</Badge> : null}
                    </span>
                    <span className="lista-simples__fraco">
                      {e.bairro?.nome ?? e.bairroTexto}
                      {e.referencia ? ` · ${e.referencia}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {/*
              Pontos antes dos pedidos: quando o cliente liga, a pergunta é
              sobre o saldo — e rolar a tela atrás dela enquanto ele espera no
              telefone é o tipo de atrito que faz o atendente desistir e
              chutar um número.
            */}
            {aberto.pontos.ativa || aberto.pontos.extrato.length > 0 ? (
              <>
                <SectionTitle variante="vazado" tamanho="sm">Pontos</SectionTitle>
                <div className="grade-stats">
                  <StatTile rotulo="Saldo" valor={String(aberto.pontos.saldo)} tom="marca" />
                  {aberto.pontos.vencendo ? (
                    <StatTile
                      rotulo="Vencendo"
                      valor={String(aberto.pontos.vencendo.pontos)}
                      apoio={`até ${dia(aberto.pontos.vencendo.em)}`}
                      tom="destaque"
                    />
                  ) : null}
                </div>

                {gestor ? (
                  <div className="linha-acoes">
                    <Button
                      variante="ghost"
                      tamanho="sm"
                      icone={Coins}
                      onClick={() => setAjustando({ pontos: '', motivo: '' })}
                    >
                      Ajustar pontos
                    </Button>
                  </div>
                ) : null}

                {aberto.pontos.extrato.length === 0 ? (
                  <p className="nota-alerta">
                    Ainda não somou ponto nenhum.
                    {aberto.pontos.ativaDesde
                      ? ` A fidelidade está ligada desde ${dia(aberto.pontos.ativaDesde)}.`
                      : ''}
                  </p>
                ) : (
                  <ul className="lista-simples">
                    {aberto.pontos.extrato.map((l) => (
                      <li key={l.id}>
                        <span>
                          {l.pontos > 0 ? '+' : '−'}
                          {Math.abs(l.pontos)}
                          <Badge tom={l.pontos > 0 ? 'sucesso' : 'erro'}>
                            {l.tipo === 'GANHO' ? 'ganhou' : l.tipo === 'AJUSTE' ? 'ajuste' : l.tipo.toLowerCase()}
                          </Badge>
                        </span>
                        <span className="lista-simples__fraco">
                          {dia(l.criadoEm)}
                          {l.numeroDia !== null
                            ? ` · pedido #${String(l.numeroDia).padStart(3, '0')}`
                            : ''}
                          {l.porQuem ? ` · por ${l.porQuem}` : ''}
                        </span>
                        {l.motivo ? (
                          <span className="lista-simples__fraco conversa__previa">{l.motivo}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : null}

            <SectionTitle variante="vazado" tamanho="sm">Últimos pedidos</SectionTitle>
            <ul className="lista-simples">
              {aberto.pedidos.map((p) => (
                <li key={p.id}>
                  <span>
                    <Icone de={ShoppingBag} tamanho={14} /> #{String(p.numeroDia).padStart(3, '0')}
                    <Badge tom={p.status === 'CANCELADO' ? 'erro' : 'neutro'}>{p.status}</Badge>
                  </span>
                  <span className="lista-simples__fraco">
                    {dia(p.criadoEm)} · {p.canal} · {formatarReais(p.totalCents)}
                  </span>
                  <span className="lista-simples__fraco conversa__previa">
                    {p.itens.map((i) => `${i.quantidade}x ${i.nomeSnapshot}`).join(', ')}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Modal>

      {/*
        Modal próprio, em cima do detalhe, e não um formulário embutido: o
        ajuste é irreversível, e um campo solto no meio da ficha é fácil de
        preencher sem perceber o peso do que está sendo feito.
      */}
      <Modal
        aberto={ajustando !== null}
        aoFechar={() => setAjustando(null)}
        titulo={`Ajustar pontos de ${aberto?.nome ?? ''}`}
        rodape={
          <>
            <Button variante="ghost" onClick={() => setAjustando(null)}>
              Deixar como está
            </Button>
            <Button
              variante="perigo"
              carregando={salvandoAjuste}
              disabled={!ajusteValido}
              onClick={() => void lancarAjuste()}
            >
              Lançar o ajuste
            </Button>
          </>
        }
      >
        <p className="nota-alerta nota-alerta--erro">
          Isso entra no extrato do cliente e não sai mais. Para corrigir depois, você lança
          outro ajuste.
        </p>
        <Input
          rotulo="Quantos pontos"
          inputMode="numeric"
          value={ajustando?.pontos ?? ''}
          onChange={(e) =>
            setAjustando((a) => (a ? { ...a, pontos: e.target.value } : a))
          }
          dica="Use o sinal de menos para tirar pontos. Exemplo: -30"
        />
        <Input
          rotulo="Por que você está ajustando?"
          value={ajustando?.motivo ?? ''}
          onChange={(e) =>
            setAjustando((a) => (a ? { ...a, motivo: e.target.value } : a))
          }
          dica="O cliente vai ler este texto no extrato dele."
        />
      </Modal>
    </div>
  )
}
